import styled from '@emotion/styled';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography, tooltipClasses } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import validator from 'validator';
import { Paths } from '../../routes';
import { DarkTextField, GradientTextField, LightTextField } from '../automated-insights/ui/TextField';

export const TEXT_FIELD_COLOR_VARIANT = {
  Dark: 'dark',
  Gradient: 'gradient',
  Light: 'light',
};

const getVariantTextField = (colorVariant) => {
  if (colorVariant === TEXT_FIELD_COLOR_VARIANT.Light) {
    return LightTextField;
  } else if (colorVariant === TEXT_FIELD_COLOR_VARIANT.Dark) {
    return DarkTextField;
  } else return GradientTextField;
};

const DisableFeaturesTooltip = styled(({ className, maxWidth, height, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, maxWidth, height }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FCFCFC',
    fontSize: '12px',
    color: theme.palette.text.primary,
    borderRadius: '24px',
    border: '1px solid #E0E0E0',
    height: height || '140px',
    minWidth: '340px',
  },
}));

const ExploreButton = styled(Button)(({ theme }) => ({
  height: '28px',
  width: '105px',
  borderRadius: '27px',
  border: '1px solid #082739',
  color: 'rgba(9, 41, 60, 1)',
}));

export default function ImportURLField({
  initialURL,
  onImportURL = null,
  isLoading = false,
  isFocused,
  onStateChange = null,
  onRemove = null,
  onFocus = null,
  helperText = null,
  showErrors = true,
  notifyErrorStates = false,
  colorVariant = TEXT_FIELD_COLOR_VARIANT.Dark,
  hasGlow,
  disableFeatures,
  freeTrialExpired,
  subscriptionExpired,
  reachedUsageLimit,
  user,
}) {
  const ref = useRef();
  const [importURL, setImportURL] = useState(initialURL ?? '');
  const [showHelperText, setShowHelperText] = useState(false);
  const [error, setError] = useState(
    initialURL ? !validator.isURL(initialURL) || validator.isEmail(initialURL) : false
  );
  const history = useHistory();
  const { track } = useAnalytics();

  const VariantTextField = getVariantTextField(colorVariant);

  const handleImportURLValueChanged = (ev) => {
    setImportURL(ev.target.value);
    const error = !validator.isURL(ev.target.value) || validator.isEmail(ev.target.value);
    ev.target.value.length === 0 ? setError(false) : setError(error);

    if (!error || notifyErrorStates) {
      // if the internal state changed, inform listeners of the new state
      // note that this input is still an "uncontrolled" input since this function is not passed to `onChange`
      onStateChange(ev.target.value);
    }
  };

  const handleInputURLKeyDown = (ev) => {
    if (error) {
      return;
    }

    if (ev.key === 'Enter' && importURL && !isLoading) {
      onImportURL && onImportURL(importURL);
    }
  };

  const redirectToSubscriptions = useCallback(() => {
    track(TrackEvent.CLICKED_BUTTON_TO_EXPLORE_PLANS_FROM_BLOCKED_FEATURE);
    history.push(`${Paths.automatedInsights.basePath}/subscription`);
  }, [history, track]);

  const getTooltipContent = useMemo(() => {
    if (disableFeatures) {
      if (freeTrialExpired) {
        return (
          <Box sx={{ marginTop: -1 }}>
            <Box mb={2} sx={{ marginLeft: 1, marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                <b>Your free trial has ended.</b>
              </Typography>
            </Box>
            <Box sx={{ marginLeft: 1, marginTop: -1 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                Ready to keep the momentum going? Subscribe now to unlock full access.
              </Typography>
            </Box>
            <Box sx={{ marginRight: 1, marginTop: 2, textAlign: 'end' }}>
              <ExploreButton onClick={redirectToSubscriptions}>
                <Typography sx={{ textTransform: 'none', fontSize: '12px' }}>
                  <b>Explore plans</b>
                </Typography>
              </ExploreButton>
            </Box>
          </Box>
        );
      } else if (subscriptionExpired) {
        return (
          <Box sx={{ marginTop: -1 }}>
            <Box mb={2} sx={{ marginLeft: 1, marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                <b>Subscription Expired</b>
              </Typography>
            </Box>
            <Box sx={{ marginLeft: 1, marginTop: -1 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                {user?.isBillingAdmin
                  ? `Your subscription to Pulse has expired. Upgrade to a full subscription today and continue transforming your digital assets with the power of WEVO Pulse.`
                  : `Your subscription to Pulse has expired. Please contact your administrator to upgrade to 
                  a full subscription and continue transforming your digital assets with the power of WEVO Pulse.`}
              </Typography>
            </Box>
            {user?.isBillingAdmin && (
              <Box sx={{ marginRight: 1, textAlign: 'end' }}>
                <ExploreButton onClick={redirectToSubscriptions}>
                  <Typography sx={{ textTransform: 'none', fontSize: '12px' }}>
                    <b>Explore plans</b>
                  </Typography>
                </ExploreButton>
              </Box>
            )}
          </Box>
        );
      } else if (reachedUsageLimit) {
        return (
          <Box sx={{ marginTop: -1 }}>
            <Box mb={2} sx={{ marginLeft: 1, marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                <b>Usage Limit Reached!</b>
              </Typography>
            </Box>
            <Box sx={{ marginLeft: 1, marginTop: -1 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                You’ve reached your usage limit for the current plan. Upgrade to unlock additional capacity.
              </Typography>
            </Box>
            <Box sx={{ marginRight: 1, marginTop: 2, textAlign: 'end' }}>
              <ExploreButton onClick={redirectToSubscriptions}>
                <Typography sx={{ textTransform: 'none', fontSize: '12px' }}>
                  <b>Explore plans</b>
                </Typography>
              </ExploreButton>
            </Box>
          </Box>
        );
      }
      return '';
    }
    return '';
  }, [
    disableFeatures,
    freeTrialExpired,
    reachedUsageLimit,
    redirectToSubscriptions,
    subscriptionExpired,
    user?.isBillingAdmin,
  ]);

  useEffect(() => {
    // since this is an uncontrolled component and we want to respect initialURL every time the prop changes,
    // we need to set the state and set the ref's value so it displays properly
    setImportURL(initialURL || '');

    if (ref?.current) {
      ref.current.value = initialURL || '';
    }
  }, [initialURL]);

  const getDisableFeaturesTooltipHeight = () => {
    if (subscriptionExpired) {
      return user?.isBillingAdmin ? '160px' : '130px';
    }

    return '';
  };

  return (
    <Box>
      <DisableFeaturesTooltip title={getTooltipContent} height={getDisableFeaturesTooltipHeight()}>
        <VariantTextField
          inputRef={ref}
          name="importURL"
          placeholder="URL"
          variant="outlined"
          size="medium"
          isFocused={isFocused}
          type="url"
          fullWidth
          glow={hasGlow}
          defaultValue={importURL}
          onChange={handleImportURLValueChanged}
          onKeyDown={handleInputURLKeyDown}
          onFocus={onFocus ? onFocus : () => setShowHelperText(true)}
          disabled={isLoading || disableFeatures}
          InputProps={{
            endAdornment: (
              <Box>
                <CircularProgress size={16} sx={{ display: isLoading ? 'initial' : 'none' }} />
                <IconButton
                  onClick={onRemove}
                  color="primary"
                  disableRipple
                  sx={{
                    display: !isLoading && onRemove ? 'flex' : 'none',
                    alignItems: 'center',
                    textTransform: 'none',
                    opacity: 0.6,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      opacity: 0.8,
                    },
                    '&:focus': {
                      opacity: 0.8,
                    },
                  }}>
                  <CancelRoundedIcon style={{ height: '20px', width: '20px' }} />
                </IconButton>
              </Box>
            ),
          }}
          sx={{ input: { cursor: disableFeatures && 'not-allowed' } }}
        />
      </DisableFeaturesTooltip>
      {!error && helperText && showHelperText && importURL && (
        <Box textAlign="right" my={1}>
          <Typography variant="caption" color="primary">
            {helperText}
          </Typography>
        </Box>
      )}
      {error && showErrors && (
        <Box textAlign="right" my={1}>
          <Typography variant="caption" color="error">
            Please input a valid url
          </Typography>
        </Box>
      )}
    </Box>
  );
}
